import React, { Component, createRef, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";

import "./Modal.scss";

const ModalContext = createContext();
class Modal extends Component {
	constructor(props) {
		super(props);
		const { show } = props;

		this.state = { closing: false, visible: show };
		this._modal = createRef();
		this.prevFocus = null;
	}

	componentDidMount() {
		const { show } = this.props;
		if (show) this.openModal();

		this._ismounted = true;
	}

	componentDidUpdate({ show: prevShow }) {
		const { visible } = this.state;
		const { show } = this.props;
		if (!prevShow && show) this.openModal();
		else if (prevShow && !show && visible) this.closeModal();
	}

	componentWillUnmount() {
		const { visible } = this.state;
		const { show } = this.props;
		this._ismounted = false;
		if (show && visible) this.closeModal();
	}

	/** Returns the width of the browser's scrollbar in pixels so that there is no layout shift when a modal is opened */
	getScrollbarSize = () => {
		const scrollDiv = document.createElement("div");
		scrollDiv.style.position = "absolute";
		scrollDiv.style.top = "-9999px";
		scrollDiv.style.width = "50px";
		scrollDiv.style.height = "50px";
		scrollDiv.style.overflow = "scroll";
		document.body.appendChild(scrollDiv);
		const size = scrollDiv.offsetWidth - scrollDiv.clientWidth;
		document.body.removeChild(scrollDiv);

		return size;
	};

	/** Indicates this modal is the modal that is currently being shown onscreen */
	isTopModal = () => {
		const modals = document.querySelectorAll(".rk-modal > .modal");
		return modals.length && modals[modals.length - 1] === this._modal.current;
	};

	/**
	 * Event listener for handling Escape button presses while the modal is open
	 * @param {Event} The HTML event handled by this method
	 */
	keyDownListener = ({ key }) => {
		const { keyboard, backdrop } = this.props;
		if (this.isTopModal() && keyboard && backdrop === true && ["Esc", "Escape"].includes(key))
			this.closeModal(true);
	};

	/** Event listener for focusing into the modal */
	enforceFocus = () => {
		const { visible } = this.state;
		const { show, enforceFocus } = this.props;
		const isMounted = show && visible;
		if (!enforceFocus || !isMounted || !this.isTopModal()) return;

		if (this._modal.current && !this._modal.current.contains(document.activeElement)) {
			this._modal.current.focus();
		}
	};

	openModal = () => {
		const { onShow, onEnter, autoFocus, onEntered, animation } = this.props;
		onShow();
		onEnter();
		this.setState({ visible: true }, () => {
			if (!document.body.classList.contains("modal-open")) {
				const isBodyOverflowing =
					document.body.clientWidth < document.defaultView.innerWidth;
				document.body.classList.add("modal-open");
				if (isBodyOverflowing) {
					document.body.style.cssText = `padding-right: ${this.getScrollbarSize()}px`;
				}
			}
			const timer = setTimeout(
				() => {
					clearTimeout(timer);
					document.addEventListener("focusin", this.enforceFocus);
					document.addEventListener("keydown", this.keyDownListener);
					if (
						autoFocus &&
						this._modal.current &&
						!this._modal.current.contains(document.activeElement)
					) {
						this.prevFocus = document.activeElement;
						this._modal.current.focus();
					}
					onEntered();
				},
				animation ? 300 : 0,
			);
		});
	};

	closeModal = (isEvent = false) => {
		const { onExit, onHide, autoFocus, restoreFocus, onExited, animation } = this.props;
		onExit();
		this.setState({ closing: true }, () => {
			const timer = setTimeout(
				() => {
					clearTimeout(timer);
					document.removeEventListener("focusin", this.enforceFocus);
					document.removeEventListener("keydown", this.keyDownListener);
					if ((autoFocus || restoreFocus) && this.prevFocus && this.prevFocus.focus) {
						this.prevFocus.focus();
					}
					const totalModals = document.querySelectorAll(".rk-modal > .modal").length;
					if (totalModals <= 1 && document.body.classList.contains("modal-open")) {
						document.body.classList.remove("modal-open");
						document.body.style.removeProperty("padding-right");
					}
					if (this._ismounted)
						this.setState({ visible: false, closing: false }, () => {
							if (isEvent) onHide();
							onExited();
						});
				},
				animation ? 300 : 0,
			);
		});
	};

	ModalFramework = () => {
		const { closing, visible } = this.state;
		const {
			id,
			backdrop,
			show,
			className,
			animation,
			dialogClassName,
			size,
			onHide,
			children,
		} = this.props;

		return (
			<div
				className="rk-modal"
				id={id}
				role="dialog"
			>
				<div
					className={classNames("modal-backdrop", {
						fade: animation,
						in: visible && !closing,
						transparent: !backdrop && show,
						hidden: !backdrop,
						closing,
					})}
				/>
				{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
				<div
					ref={this._modal}
					className={classNames("modal", className, {
						fade: animation,
						in: visible && !closing,
						closing,
					})}
					role="dialog"
					tabIndex="-1"
					aria-labelledby={id ? `${id}ModalLabel` : undefined}
					onClick={({ target, currentTarget }) => {
						if (target !== currentTarget) return;
						if (backdrop === true) this.closeModal(true);
					}}
					onKeyPress={null}
				>
					<div
						role="document"
						className={classNames("modal-dialog", dialogClassName, {
							"modal-sm": size === "sm",
							"modal-lg": size === "lg",
							"modal-xl": size === "xl",
						})}
					>
						<div className="modal-content">
							<ModalContext.Provider value={onHide}>{children}</ModalContext.Provider>
						</div>
					</div>
				</div>
			</div>
		);
	};

	render() {
		const { visible } = this.state;
		const { show, animation } = this.props;
		if (show || (animation && visible))
			return createPortal(<this.ModalFramework />, document.body);
		return null;
	}
}

Modal.propTypes = {
	children: PropTypes.any.isRequired,
	backdrop: PropTypes.oneOf(["static", true, false]),
	size: PropTypes.oneOf(["sm", "lg", "xl"]),
	id: PropTypes.string,
	className: PropTypes.string,
	dialogClassName: PropTypes.string,
	show: PropTypes.bool,
	keyboard: PropTypes.bool,
	animation: PropTypes.bool,
	autoFocus: PropTypes.bool,
	enforceFocus: PropTypes.bool,
	restoreFocus: PropTypes.bool,
	onShow: PropTypes.func, // fired when modal is opening
	onHide: PropTypes.func, // fired when the non-static backdrop is clicked
	onEnter: PropTypes.func, // Pre-transition
	onEntered: PropTypes.func, // Post-transition
	onExit: PropTypes.func, // Pre-transition
	onExited: PropTypes.func, // Post-transition
};

Modal.defaultProps = {
	backdrop: true,
	size: undefined,
	id: undefined,
	className: undefined,
	dialogClassName: undefined,
	show: false,
	keyboard: true,
	animation: true,
	autoFocus: true,
	enforceFocus: true,
	restoreFocus: true,
	onShow: () => {},
	onHide: () => {},
	onEnter: () => {},
	onEntered: () => {},
	onExit: () => {},
	onExited: () => {},
};

// Until the react-bootstrap modal is completely unused, these sub-components will make the transition much easier
// TODO: Replace these with the semantic HTML
const subPropTypes = { children: PropTypes.any, className: PropTypes.string, id: PropTypes.string };
const subDefaultProps = { children: undefined, className: undefined, id: undefined };

Modal.Header = ({ className, children, id, closeButton }) => {
	const onHide = useContext(ModalContext);
	return (
		<div
			className={classNames("modal-header", className)}
			id={id}
		>
			<If condition={closeButton}>
				<button
					type="button"
					className="close"
					aria-label="Close"
					onClick={onHide}
				>
					<span aria-hidden="true">&times;</span>
				</button>
			</If>
			{children}
		</div>
	);
};
Modal.Header.propTypes = { ...subPropTypes, closeButton: PropTypes.bool };
Modal.Header.defaultProps = { ...subDefaultProps, closeButton: false };

Modal.Title = ({ className, children, id }) => (
	<h4
		className={classNames("modal-title text-center", className)}
		id={id}
	>
		{children}
	</h4>
);
Modal.Title.propTypes = subPropTypes;
Modal.Title.defaultProps = subDefaultProps;

Modal.Body = ({ className, children, id }) => (
	<div
		className={classNames("modal-body", className)}
		id={id}
	>
		{children}
	</div>
);
Modal.Body.propTypes = subPropTypes;
Modal.Body.defaultProps = subDefaultProps;

Modal.Footer = ({ className, children, id }) => (
	<div
		className={classNames("modal-footer text-center", className)}
		id={id}
	>
		{children}
	</div>
);
Modal.Footer.propTypes = subPropTypes;
Modal.Footer.defaultProps = subDefaultProps;

export default Modal;
